import { CheckCircle2, XCircle } from "lucide-react";
import type { ReactNode } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Alert } from "../../elements/alert/alert";
import { Button } from "../../elements/button/button";
import { Text } from "../../elements/text/text";
import { IconLock } from "../../icons/lock-icon";
import {
  ALLOWED_CHARS_REGEX,
  ALLOWED_SPECIAL_CHARS,
} from "../../utils/password-validator";
import type { UserRole } from "../user-card-profile/user-card-profile";
import { UserFormField } from "../user-form-modal/user-form-field";
import {
  PASSWORD_MIN_LENGTH,
  type PasswordFormData,
} from "./user-card-security.schema";

function hasAllowedSpecialChar(value: string): boolean {
  return value.split("").some((char) => ALLOWED_SPECIAL_CHARS.includes(char));
}

function PasswordRequirement(props: {
  met: boolean;
  label: string;
}): ReactNode {
  const Icon = props.met ? CheckCircle2 : XCircle;
  const color = props.met ? "muted" : "warning";

  return (
    <div className="flex items-center gap-1.5">
      <Icon
        aria-hidden="true"
        className={props.met ? "text-muted-foreground" : "text-orange-500"}
        size={16}
      />
      <Text color={color} variant="body-xs">
        {props.label}
      </Text>
    </div>
  );
}

export interface UserCardSecurityProps {
  microcopies: {
    title: string;
    description: string;
    currentPasswordLabel: string;
    newPasswordLabel: string;
    newPasswordHint: string;
    confirmNewPasswordLabel: string;
    changePasswordButton: string;
    reqMinChars: string;
    reqNumber: string;
    reqUpperLower: string;
    reqSpecialChar: string;
    passwordMismatchTitle: string;
    passwordMismatchMessage: string;
    serverErrorTitle?: string;
    incorrectCurrentPasswordError?: string;
  };
  onChangePassword?: (data: {
    currentPassword: string;
    newPassword: string;
    confirmNewPassword: string;
  }) => void | Promise<void>;
  userRole?: UserRole;
  loading?: boolean;
  disabled?: boolean;
  /** Friendly, localized message from a failed password-change request, e.g. "Current password is incorrect". Shown inline; fields keep their values so the user can retry. */
  serverError?: string;
}

export function UserCardSecurity(
  props: Readonly<UserCardSecurityProps>
): ReactNode {
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isValid },
  } = useForm<PasswordFormData>({
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
    mode: "onChange",
  });

  const newPassword = useWatch({ control, name: "newPassword" });

  const min8 = newPassword.length >= PASSWORD_MIN_LENGTH;
  const hasNum = /\d/.test(newPassword);
  const hasUpperLower = /[a-z]/.test(newPassword) && /[A-Z]/.test(newPassword);
  const hasSpecial = hasAllowedSpecialChar(newPassword);

  const canEdit = props.userRole !== "UNKNOWN";

  const serverErrorTitle =
    props.microcopies.serverErrorTitle ?? props.serverError;
  const serverErrorDescription = props.microcopies.serverErrorTitle
    ? props.serverError
    : undefined;

  async function onSubmit(data: PasswordFormData) {
    try {
      await props.onChangePassword?.(data);
      reset({
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: "",
      });
    } catch {
      // Keep the form with its current values; the caller surfaces the
      // failure via `serverError` instead of resetting as though it succeeded.
    }
  }

  return (
    <article className="flex flex-col gap-6 rounded-md bg-white p-4">
      <header className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <IconLock className="h-6 w-6 text-muted-foreground" />
          <Text variant="h4">{props.microcopies.title}</Text>
        </div>
        <div className="flex flex-col gap-1">
          <Text color="black" variant="body-md">
            {props.microcopies.description}
          </Text>
        </div>
      </header>

      <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
        <UserFormField
          autoComplete="current-password"
          disabled={props.disabled || !canEdit}
          error={errors.currentPassword}
          id="currentPassword"
          label={props.microcopies.currentPasswordLabel}
          labelClassName="font-semibold text-zinc-900"
          registerProps={register("currentPassword", {
            required: "Current password is required",
          })}
          type="password"
        />

        <UserFormField
          autoComplete="new-password"
          disabled={props.disabled || !canEdit}
          error={errors.newPassword}
          id="newPassword"
          label={props.microcopies.newPasswordLabel}
          labelClassName="font-semibold text-zinc-900"
          registerProps={register("newPassword", {
            required: "New password is required",
            minLength: {
              value: PASSWORD_MIN_LENGTH,
              message: "Minimum 8 characters",
            },
            validate: {
              hasUpper: (val) =>
                /[A-Z]/.test(val) || "At least 1 uppercase letter",
              hasLower: (val) =>
                /[a-z]/.test(val) || "At least 1 lowercase letter",
              hasNum: (val) => /\d/.test(val) || "At least 1 number",
              hasSpecial: (val) =>
                hasAllowedSpecialChar(val) || "At least 1 special character",
              hasValidChars: (val) =>
                ALLOWED_CHARS_REGEX.test(val) ||
                `Only letters, numbers, and ${ALLOWED_SPECIAL_CHARS.split("").join(" ")} are allowed, including as the last character`,
            },
          })}
          type="password"
        >
          {newPassword.length === 0 && !errors.newPassword ? (
            <Text color="muted" variant="body-xs">
              {props.microcopies.newPasswordHint}
            </Text>
          ) : (
            <div className="mt-1 flex flex-col gap-1">
              <PasswordRequirement
                label={props.microcopies.reqMinChars}
                met={min8}
              />
              <PasswordRequirement
                label={props.microcopies.reqNumber}
                met={hasNum}
              />
              <PasswordRequirement
                label={props.microcopies.reqUpperLower}
                met={hasUpperLower}
              />
              <PasswordRequirement
                label={props.microcopies.reqSpecialChar}
                met={hasSpecial}
              />
            </div>
          )}
        </UserFormField>

        <UserFormField
          autoComplete="new-password"
          disabled={props.disabled || !canEdit}
          error={errors.confirmNewPassword}
          id="confirmNewPassword"
          label={props.microcopies.confirmNewPasswordLabel}
          labelClassName="font-semibold"
          registerProps={register("confirmNewPassword", {
            required: "Please confirm your new password",
            validate: (val) => val === newPassword,
          })}
          type="password"
        />

        {errors.confirmNewPassword?.type === "validate" && (
          <Alert
            microcopies={{
              title: props.microcopies.passwordMismatchTitle,
              description: props.microcopies.passwordMismatchMessage,
            }}
            variant="warning"
          />
        )}

        {props.serverError && (
          <Alert
            microcopies={{
              title: serverErrorTitle ?? props.serverError,
              description: serverErrorDescription,
            }}
            variant="destructive"
          />
        )}

        <footer className="mt-2 flex items-center">
          <Button
            disabled={!(isValid && canEdit)}
            loading={props.loading}
            size="sm"
            type="submit"
            variant="default"
          >
            {props.microcopies.changePasswordButton}
          </Button>
        </footer>
      </form>
    </article>
  );
}
